// src/pages/finance-portal/cash-alerts/MinimumBalanceAlerts.js
//
// Minimum Balance Alerting

import React, { useCallback, useEffect, useState } from 'react';
import {
    Alert, Box, Button, Card, CardContent, Checkbox, Chip, CircularProgress,
    Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel,
    Grid, IconButton, MenuItem, Skeleton, Stack, Table, TableBody, TableCell,
    TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import NotificationsOffIcon from '@mui/icons-material/NotificationsOff';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import SnoozeIcon from '@mui/icons-material/Snooze';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#3b82f6';

const STATUS_COLORS = {
    ACTIVE: { bg: '#dcfce7', color: '#166534' },
    TRIGGERED: { bg: '#fee2e2', color: '#991b1b' },
    RESOLVED: { bg: '#dbeafe', color: '#1e40af' },
    SNOOZED: { bg: '#fef3c7', color: '#92400e' },
    DISABLED: { bg: '#f1f5f9', color: '#475569' },
};

const SEVERITY_COLORS = {
    WARNING: '#f59e0b',
    CRITICAL: '#ef4444',
};

const CHANNEL_LABELS = {
    EMAIL: 'Email', SMS: 'SMS', IN_APP: 'In-App', SLACK: 'Slack',
};

function formatCurrency(amount) {
    if (amount == null) return '$0.00';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
}

const EMPTY_FORM = {
    stripe_account_id: '',
    warning_threshold: '',
    critical_threshold: '',
    notification_channels: ['EMAIL', 'IN_APP'],
    forecast_horizon_days: 7,
    forecast_enabled: true,
};

export default function MinimumBalanceAlerts() {
    const [alerts, setAlerts] = useState([]);
    const [accounts, setAccounts] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isEvaluating, setIsEvaluating] = useState(false);
    const [error, setError] = useState(null);
    const [dialog, setDialog] = useState({ open: false, form: { ...EMPTY_FORM }, isEdit: false });

    const fetchAll = useCallback(async () => {
        setError(null);
        setIsLoading(true);
        try {
            const [alertRes, acctRes] = await Promise.all([
                axiosInstance.get('/api/finance/cash-position/alerts'),
                axiosInstance.get('/api/finance/stripe/accounts').catch(() => ({ data: { data: [] } })),
            ]);
            if (alertRes.data?.success) setAlerts(alertRes.data.data || []);
            setAccounts(acctRes.data?.data || []);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load alerts.');
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => { fetchAll(); }, [fetchAll]);

    const handleEvaluate = async () => {
        setIsEvaluating(true);
        try {
            await axiosInstance.post('/api/finance/cash-position/alerts/evaluate');
            await fetchAll();
        } catch (err) {
            setError(err.response?.data?.message || 'Evaluation failed.');
        } finally {
            setIsEvaluating(false);
        }
    };

    const openCreate = () => setDialog({ open: true, form: { ...EMPTY_FORM }, isEdit: false });
    const openEdit = (alert) => setDialog({
        open: true,
        isEdit: true,
        form: {
            stripe_account_id: alert.stripe_account_id,
            warning_threshold: alert.warning_threshold,
            critical_threshold: alert.critical_threshold,
            notification_channels: alert.notification_channels || [],
            forecast_horizon_days: alert.forecast_horizon_days || 7,
            forecast_enabled: alert.forecast_enabled,
        },
    });

    const handleSave = async () => {
        try {
            await axiosInstance.post('/api/finance/cash-position/alerts', dialog.form);
            setDialog({ open: false, form: { ...EMPTY_FORM }, isEdit: false });
            await fetchAll();
        } catch (err) {
            setError(err.response?.data?.message || 'Save failed.');
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Delete this alert? This action cannot be undone.')) return;
        try {
            await axiosInstance.delete(`/api/finance/cash-position/alerts/${id}`);
            await fetchAll();
        } catch (err) {
            setError(err.response?.data?.message || 'Delete failed.');
        }
    };

    const handleSnooze = async (id) => {
        try {
            await axiosInstance.post(`/api/finance/cash-position/alerts/${id}/snooze`, { hours: 24 });
            await fetchAll();
        } catch (err) {
            setError(err.response?.data?.message || 'Snooze failed.');
        }
    };

    const triggered = alerts.filter(a => a.status === 'TRIGGERED').length;
    const active = alerts.filter(a => a.status === 'ACTIVE').length;

    return (
        <Box sx={{ p: { xs: 2, md: 3 }, fontFamily, minHeight: '100vh', bgcolor: '#f8fafc' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                <Box>
                    <Typography sx={{ fontFamily, fontWeight: 800, fontSize: '1.6rem', color: '#0f172a' }}>
                        Minimum Balance Alerts
                    </Typography>
                    <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#64748b', mt: 0.5 }}>
                        Proactive low-balance warnings. Forecasts the next 7 days when enabled.
                    </Typography>
                </Box>
                <Stack direction="row" spacing={1.5}>
                    <Button
                        variant="outlined"
                        startIcon={isEvaluating ? <CircularProgress size={14} /> : <PlayArrowIcon />}
                        onClick={handleEvaluate}
                        disabled={isEvaluating}
                        sx={{ fontFamily, textTransform: 'none', borderRadius: '10px' }}
                    >
                        Evaluate Now
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={openCreate}
                        sx={{ fontFamily, textTransform: 'none', borderRadius: '10px', bgcolor: ACCENT }}
                    >
                        New Alert
                    </Button>
                    <IconButton onClick={fetchAll}><RefreshIcon /></IconButton>
                </Stack>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            <Grid container spacing={2.5} sx={{ mb: 3 }}>
                {[
                    { label: 'Triggered Now', value: triggered, color: '#ef4444', icon: <NotificationsActiveIcon /> },
                    { label: 'Active', value: active, color: '#10b981', icon: <NotificationsActiveIcon /> },
                    { label: 'Total Configured', value: alerts.length, color: ACCENT, icon: <NotificationsActiveIcon /> },
                ].map((s) => (
                    <Grid item xs={12} sm={4} key={s.label}>
                        <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                            <CardContent sx={{ p: 2.5, display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Box sx={{ width: 44, height: 44, borderRadius: '12px', bgcolor: `${s.color}14`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: s.color }}>
                                    {s.icon}
                                </Box>
                                <Box>
                                    <Typography sx={{ fontFamily, fontSize: '0.7rem', color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase' }}>
                                        {s.label}
                                    </Typography>
                                    <Typography sx={{ fontFamily, fontWeight: 800, fontSize: '1.5rem', color: '#0f172a' }}>
                                        {isLoading ? <Skeleton width={40} /> : s.value}
                                    </Typography>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                <CardContent sx={{ p: 0 }}>
                    {isLoading ? (
                        <Box sx={{ p: 3 }}><Skeleton variant="rounded" height={300} /></Box>
                    ) : (
                        <Table>
                            <TableHead>
                                <TableRow sx={{ '& th': { fontFamily, fontWeight: 700, color: '#475569', bgcolor: '#f8fafc' } }}>
                                    <TableCell>Account</TableCell>
                                    <TableCell align="right">Warning</TableCell>
                                    <TableCell align="right">Critical</TableCell>
                                    <TableCell align="right">Last Balance</TableCell>
                                    <TableCell>Channels</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell align="right">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {alerts.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={7} sx={{ textAlign: 'center', py: 5, fontFamily, color: '#94a3b8' }}>
                                            No alerts yet. Click <b>New Alert</b> to set thresholds for an account.
                                        </TableCell>
                                    </TableRow>
                                )}
                                {alerts.map((a) => (
                                    <TableRow key={a.id} hover>
                                        <TableCell sx={{ fontFamily }}>
                                            <Typography sx={{ fontFamily, fontWeight: 600, fontSize: '0.85rem' }}>
                                                {a.account_name || `Account #${a.stripe_account_id}`}
                                            </Typography>
                                            {a.account_mask && (
                                                <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#94a3b8' }}>
                                                    {a.institution_name} ••••{a.account_mask}
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell align="right" sx={{ fontFamily, color: SEVERITY_COLORS.WARNING, fontWeight: 700 }}>
                                            {formatCurrency(a.warning_threshold)}
                                        </TableCell>
                                        <TableCell align="right" sx={{ fontFamily, color: SEVERITY_COLORS.CRITICAL, fontWeight: 700 }}>
                                            {formatCurrency(a.critical_threshold)}
                                        </TableCell>
                                        <TableCell align="right" sx={{ fontFamily, fontWeight: 700, color: '#0f172a' }}>
                                            {a.last_balance_seen != null ? formatCurrency(a.last_balance_seen) : '—'}
                                        </TableCell>
                                        <TableCell>
                                            <Stack direction="row" spacing={0.5} flexWrap="wrap">
                                                {(a.notification_channels || []).map((c) => (
                                                    <Chip key={c} label={CHANNEL_LABELS[c] || c} size="small" sx={{ fontFamily, fontSize: '0.65rem', bgcolor: '#f1f5f9' }} />
                                                ))}
                                            </Stack>
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={a.status}
                                                size="small"
                                                sx={{
                                                    fontFamily, fontWeight: 700, fontSize: '0.7rem',
                                                    bgcolor: STATUS_COLORS[a.status]?.bg, color: STATUS_COLORS[a.status]?.color,
                                                }}
                                            />
                                            {a.last_severity && (
                                                <Typography sx={{ fontFamily, fontSize: '0.7rem', color: SEVERITY_COLORS[a.last_severity], mt: 0.5 }}>
                                                    {a.last_severity}
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell align="right">
                                            <Tooltip title="Snooze 24 hours">
                                                <IconButton size="small" onClick={() => handleSnooze(a.id)}>
                                                    <SnoozeIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <IconButton size="small" onClick={() => openEdit(a)}>
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                            <IconButton size="small" onClick={() => handleDelete(a.id)}>
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </CardContent>
            </Card>

            <Dialog open={dialog.open} onClose={() => setDialog({ open: false, form: { ...EMPTY_FORM }, isEdit: false })} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>
                    {dialog.isEdit ? 'Edit Alert' : 'New Minimum Balance Alert'}
                </DialogTitle>
                <DialogContent>
                    <Stack spacing={2.5} sx={{ mt: 1 }}>
                        <TextField
                            select
                            label="Account"
                            value={dialog.form.stripe_account_id || ''}
                            onChange={(e) => setDialog({ ...dialog, form: { ...dialog.form, stripe_account_id: e.target.value } })}
                            disabled={dialog.isEdit}
                            fullWidth
                        >
                            {accounts.map((acct) => (
                                <MenuItem key={acct.id} value={acct.id}>
                                    {acct.name || acct.official_name || 'Account'} {acct.mask ? `••••${acct.mask}` : ''}
                                </MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            label="Warning Threshold"
                            type="number"
                            value={dialog.form.warning_threshold}
                            onChange={(e) => setDialog({ ...dialog, form: { ...dialog.form, warning_threshold: e.target.value } })}
                            helperText="Notify when balance falls at or below this amount."
                            fullWidth
                        />
                        <TextField
                            label="Critical Threshold"
                            type="number"
                            value={dialog.form.critical_threshold}
                            onChange={(e) => setDialog({ ...dialog, form: { ...dialog.form, critical_threshold: e.target.value } })}
                            helperText="Hard alert — escalates to all configured channels."
                            fullWidth
                        />
                        <TextField
                            label="Notification Channels (comma separated)"
                            value={(dialog.form.notification_channels || []).join(', ')}
                            onChange={(e) => setDialog({
                                ...dialog,
                                form: {
                                    ...dialog.form,
                                    notification_channels: e.target.value.split(',').map(s => s.trim().toUpperCase()).filter(Boolean),
                                },
                            })}
                            helperText="Allowed: EMAIL, SMS, IN_APP, SLACK"
                            fullWidth
                        />
                        <TextField
                            label="Forecast Horizon (days)"
                            type="number"
                            value={dialog.form.forecast_horizon_days}
                            onChange={(e) => setDialog({ ...dialog, form: { ...dialog.form, forecast_horizon_days: parseInt(e.target.value, 10) || 0 } })}
                            inputProps={{ min: 0, max: 90 }}
                            fullWidth
                        />
                        <FormControlLabel
                            control={(
                                <Checkbox
                                    checked={dialog.form.forecast_enabled}
                                    onChange={(e) => setDialog({ ...dialog, form: { ...dialog.form, forecast_enabled: e.target.checked } })}
                                />
                            )}
                            label="Use forecast lookahead (alert if projected to fall below)"
                            sx={{ '& .MuiFormControlLabel-label': { fontFamily } }}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialog({ open: false, form: { ...EMPTY_FORM }, isEdit: false })} sx={{ fontFamily, textTransform: 'none' }}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleSave}
                        disabled={!dialog.form.stripe_account_id}
                        sx={{ fontFamily, textTransform: 'none', bgcolor: ACCENT }}
                    >
                        Save Alert
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
